import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { SkeletonBlockComponent } from '@components';
import { ReposFacade } from '@state';
import { IntegrationsService, ReposGitHubService, GitHubRepoSummary } from '@services';
import { RepoRole } from '@state';
import { ToastService } from '@services';

function defaultGitignore(kind: string): string {
  const k = String(kind ?? '').trim().toLowerCase();
  if (k === 'node' || k === 'typescript' || k === 'javascript' || k === 'angular') {
    return ['node_modules/', 'dist/', 'build/', '.env', '.env.*', '*.log', '.DS_Store'].join('\n');
  }
  return ['*.log', '.DS_Store', '.env', '.env.*'].join('\n');
}

@Component({
  selector: 'app-repos-workspace',
  standalone: true,
  imports: [FormsModule, SkeletonBlockComponent],
  styles: [
    `
      /* Make Bootstrap helper text consistent with our theme tokens */
      :host .text-muted {
        color: var(--text-muted) !important;
        opacity: 1 !important;
      }
      :host code {
        color: var(--text-primary);
        background: color-mix(in srgb, var(--color-border) 35%, transparent);
        border: 1px solid color-mix(in srgb, var(--color-border) 75%, transparent);
        padding: 0.05rem 0.35rem;
        border-radius: 0.35rem;
      }
      :host .nav-tabs {
        border-bottom-color: color-mix(in srgb, var(--color-border) 85%, transparent);
      }
      :host .nav-tabs .nav-link {
        color: var(--text-muted);
        border-color: transparent;
      }
      :host .nav-tabs .nav-link:hover {
        color: var(--text-primary);
        border-color: color-mix(in srgb, var(--color-border) 70%, transparent);
      }
      :host .nav-tabs .nav-link.active {
        color: var(--text-primary);
        background: color-mix(in srgb, var(--color-surface) 85%, var(--color-border) 15%);
        border-color: color-mix(in srgb, var(--color-border) 85%, transparent);
        border-bottom-color: var(--color-surface);
      }
      :host .repos-install-row .form-control-sm,
      :host .repos-install-row .form-select-sm {
        min-height: calc(1.5em + 0.5rem + 2px);
      }
      :host .repos-inline-card {
        border: 1px solid color-mix(in srgb, var(--color-border) 70%, transparent);
        border-radius: 0.6rem;
      }
      :host .card {
        background: color-mix(in srgb, var(--color-surface) 96%, var(--color-border) 4%);
        border: 1px solid color-mix(in srgb, var(--color-border) 72%, transparent) !important;
        border-radius: 0.8rem;
      }
      :host .form-control,
      :host .form-select {
        background-color: var(--color-surface);
        color: var(--text-primary);
        border: 1px solid color-mix(in srgb, var(--color-border) 82%, transparent);
      }
      :host .form-control:focus,
      :host .form-select:focus {
        background-color: var(--color-surface);
        color: var(--text-primary);
        border-color: var(--primary-color);
        box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--primary-color) 20%, transparent);
      }
      :host .btn-outline-secondary,
      :host .btn-outline-primary {
        color: var(--text-primary);
        border-color: color-mix(in srgb, var(--color-border) 82%, transparent);
        background: color-mix(in srgb, var(--color-surface) 94%, transparent);
      }
      :host .btn-outline-secondary:hover,
      :host .btn-outline-primary:hover {
        color: var(--text-primary);
        border-color: color-mix(in srgb, var(--primary-color) 45%, var(--color-border));
        background: color-mix(in srgb, var(--color-surface) 84%, var(--primary-color) 16%);
      }
      :host .btn-primary {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: var(--primary-color-contrast, #fff);
      }
      :host .btn-primary:hover {
        background: color-mix(in srgb, var(--primary-color) 88%, #000);
        border-color: color-mix(in srgb, var(--primary-color) 88%, #000);
      }
      :host .alert-danger {
        color: var(--text-primary);
        background: color-mix(in srgb, #b42318 10%, var(--color-surface));
        border: 1px solid color-mix(in srgb, #b42318 35%, var(--color-border));
      }
      :host .badge.text-bg-primary {
        background: var(--primary-color) !important;
        color: var(--primary-color-contrast, #fff) !important;
      }
      :host .repos-project-repo-btn {
        background: color-mix(in srgb, var(--color-surface) 96%, var(--color-border) 4%);
      }
      :host .repos-project-repo-btn:hover {
        background: color-mix(in srgb, var(--color-surface) 86%, var(--primary-color) 14%);
      }
    `,
  ],
  template: `
    @if (!reposStore.activeProjectId()) {
      <div class="card border-0 shadow-sm p-3">
        <div class="fw-semibold mb-1">Select a project</div>
        <div class="small text-muted">
          Repos are configured per project. Select a project in Workspace, then return here to connect GitHub.
        </div>
      </div>
    } @else if (reposStore.loading()) {
      <div class="card border-0 shadow-sm p-3">
        <app-skeleton-block height="14px" width="36%" rounded="8px"></app-skeleton-block>
        <div class="mt-3"></div>
        <app-skeleton-block [lines]="6" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
      </div>
    } @else {
      @if (!reposStore.hasGitHubInstallation()) {
        <div class="card border-0 shadow-sm p-3">
          <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
            <div>
              <div class="fw-semibold">Connect GitHub</div>
              <div class="mt-2">
                <div class="small">
                  Connect your personal GitHub once and new repos will be created under your own account by default.
                  If you have not connected it yet, this project will fall back to <code>{{ businessOwnerLabel }}</code>.
                </div>
              </div>
            </div>
            <div class="d-flex flex-column gap-2 align-self-lg-start">
              <button
                class="btn btn-primary btn-sm"
                type="button"
                (click)="onConnectExistingClick()"
                [disabled]="installLoading || connectExistingLoading"
              >
                @if (connectExistingLoading) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Connecting…
                } @else {
                  <i class="bi bi-github me-1"></i>
                  Connect personal GitHub
                }
              </button>
            </div>
          </div>
        </div>
      } @else {
        <div class="d-flex flex-column gap-3">
          <div class="card border-0 shadow-sm p-3">
            <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
              <div>
                <div class="fw-semibold">GitHub connection</div>
                <div class="small text-muted mt-1">
                  @if (currentInstallationMode() === 'personal') {
                    New repos will use your personal GitHub account <code>{{ currentConnectionLabel() }}</code>.
                  } @else {
                    New repos will use the organization fallback account <code>{{ currentConnectionLabel() }}</code>.
                  }
                </div>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  type="button"
                  (click)="onConnectExistingClick()"
                  [disabled]="connectExistingLoading || actionBusy"
                >
                  @if (connectExistingLoading) {
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Connecting…
                  } @else {
                    <i class="bi bi-github me-1"></i>
                    Connect personal GitHub
                  }
                </button>
                @if (currentInstallationMode() === 'personal') {
                  <button class="btn btn-outline-secondary btn-sm" type="button" (click)="onUseOrganizationClick()" [disabled]="actionBusy">
                    <i class="bi bi-building me-1"></i>
                    Use organization GitHub
                  </button>
                }
              </div>
            </div>
          </div>

          @if (projectReposDisplay().length > 0) {
            <div class="card border-0 shadow-sm p-3">
              <div class="fw-semibold">Project repos</div>
              <div class="small text-muted mt-1">
                These repos are already connected to this project. Choose one when you want to inspect its live GitHub details.
              </div>
              <div class="d-flex flex-column gap-2 mt-3">
                @for (repo of projectReposDisplay(); track repo.fullName) {
                  <button
                    class="btn btn-outline-secondary btn-sm text-start d-flex align-items-center justify-content-between gap-2 repos-project-repo-btn"
                    type="button"
                    (click)="selectProjectRepo(repo.owner, repo.name)"
                    [disabled]="actionBusy"
                  >
                    <span>{{ repo.fullName }}</span>
                    @if (selectedFullName === repo.fullName) {
                      <span class="badge text-bg-primary">Selected</span>
                    }
                  </button>
                }
              </div>
              @if (reposError) {
                <div class="alert alert-danger py-2 small mt-3 mb-0">{{ reposError }}</div>
              }
            </div>
          } @else {
            <div class="card border-0 shadow-sm p-3">
              @if (detectedProjectRepos().length > 0) {
                <div class="fw-semibold">Existing repos found</div>
                <div class="small text-muted mt-1">
                  We found repos under <code>{{ currentConnectionLabel() }}</code> that match this project. Connect them and continue.
                </div>
                <div class="small mt-2">
                  @for (repo of detectedProjectRepos(); track repo.fullName) {
                    <code class="me-1">{{ repo.fullName }}</code>
                  }
                </div>
                <div class="mt-3">
                  <button class="btn btn-primary btn-sm" type="button" (click)="connectDetectedProjectRepos()" [disabled]="actionBusy">
                    <i class="bi bi-link-45deg me-1"></i>
                    Connect found repos
                  </button>
                </div>
              } @else {
                <div class="fw-semibold">No repos connected yet</div>
                <div class="small text-muted mt-1">
                  Either import existing repos from this GitHub account, or create the standard repos for this project.
                </div>
              }
            </div>
          }

          <div class="card border-0 shadow-sm p-3">
            <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
              <div>
                <div class="fw-semibold">Import existing repos</div>
                <div class="small text-muted mt-1">
                  Available repos are loaded automatically from <code>{{ currentConnectionLabel() }}</code>.
                </div>
              </div>
              <button
                class="btn btn-outline-secondary btn-sm"
                type="button"
                (click)="loadInstallationRepos()"
                [disabled]="installationReposLoading || actionBusy"
              >
                @if (installationReposLoading) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Refreshing…
                } @else {
                  <i class="bi bi-arrow-repeat me-1"></i>
                  Refresh list
                }
              </button>
            </div>

            <div class="row g-2 mt-2 align-items-end repos-install-row">
              <div class="col-12 col-lg-8">
                <label class="form-label small text-muted mb-1">Available repos</label>
                <select class="form-select form-select-sm" [(ngModel)]="installationSelectedFullName">
                  <option value="">— Select a repository —</option>
                  @for (r of importableInstallationRepos(); track r.id) {
                    <option [value]="r.fullName">{{ r.fullName }}</option>
                  }
                </select>
              </div>
              <div class="col-12 col-lg-4 d-flex justify-content-lg-end">
                <button
                  class="btn btn-primary btn-sm"
                  type="button"
                  (click)="addInstallationSelectedRepoToProject()"
                  [disabled]="actionBusy || !canAddInstallationSelectedRepoToProject()"
                >
                  <i class="bi bi-plus-circle me-1"></i>
                  Add repo
                </button>
              </div>
            </div>

            @if (installationReposError) {
              <div class="alert alert-danger py-2 small mt-3 mb-0">{{ installationReposError }}</div>
            } @else if (!installationReposLoading && importableInstallationRepos().length === 0) {
              <div class="small text-muted mt-3">No additional repos are available to import from this GitHub account.</div>
            }
          </div>

          @if (projectReposDisplay().length === 0) {
            <div class="card border-0 shadow-sm p-3">
              <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                <div>
                  <div class="fw-semibold">Create standard repos</div>
                  <div class="small text-muted mt-1">
                    If this project does not already have repos, Appstrax Machine can create the standard repo set for you.
                  </div>
                </div>
                <div class="d-flex flex-column align-items-start align-items-lg-end gap-1">
                  <button
                    class="btn btn-outline-primary btn-sm"
                    type="button"
                    (click)="onCreateStandardRepos()"
                    [disabled]="standardBusy || actionBusy || !canCreateStandardRepos()"
                  >
                    @if (standardBusy) {
                      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span class="ms-2">Creating {{ standardProgress?.step ?? 0 }}/{{ standardProgress?.total ?? 0 }}…</span>
                    } @else {
                      <i class="bi bi-stack me-1"></i>
                      Create standard repos
                    }
                  </button>
                  @if (standardBusy && standardProgress?.repoName) {
                    <div class="small text-muted">
                      Currently: <code>{{ preferredRepoOwnerLabel() }}/{{ standardProgress?.repoName }}</code>
                    </div>
                  }
                </div>
              </div>
              <div class="small mt-2">
                @if (standardReposPreview.length === 0) {
                  <span class="text-muted">Select a project to generate repo names.</span>
                } @else {
                  <span class="text-muted">Will create:</span>
                  @for (n of standardReposPreview; track $index) {
                    <code class="ms-1">{{ preferredRepoOwnerLabel() + '/' + n }}</code>
                  }
                }
              </div>
            </div>
          }
        </div>
      }
    }
  `,
})
export class ReposWorkspaceComponent {
  private static readonly PERSONAL_GITHUB_INSTALLATION_ID_KEY = 'settings.personalGithubInstallationId';
  private static readonly PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY = 'settings.personalGithubAccountLogin';
  private static readonly AUTO_BOOTSTRAP_PROJECT_ID_KEY = 'repos.autoBootstrapProjectId';
  private static readonly PENDING_INSTALL_SOURCE_KEY = 'repos.pendingInstallSource';
  public reposStore = inject(ReposFacade);
  private github = inject(ReposGitHubService);
  private integrations = inject(IntegrationsService);
  private toast = inject(ToastService);

  public installLoading = false;
  public connectExistingLoading = false;
  public installUrl: string | null = null;
  public setupUrlHint: string | null = null;

  public reposLoading = false;
  public reposError: string | null = null;
  public repos: GitHubRepoSummary[] = [];

  public installationReposLoading = false;
  public installationReposError: string | null = null;
  public installationRepos: GitHubRepoSummary[] = [];
  public installationRepoSearch = '';
  public installationSelectedFullName = '';
  public businessOwnerLabel = 'the-machine';

  public selectedFullName = '';
  public refreshing = false;
  public actionBusy = false;

  public createRepo: { name: string; description: string; private: boolean } = {
    name: '',
    description: '',
    private: true,
  };

  public branch: { baseBranch: string; name: string } = { baseBranch: 'main', name: '' };
  public pr: { title: string; head: string; base: string; body: string } = { title: '', head: '', base: 'main', body: '' };
  public upsert: { branch: string; message: string; path: string; contentText: string } = {
    branch: '',
    message: 'Update repository files',
    path: '',
    contentText: '',
  };

  public standardBusy = false;
  public standardProgress: { step: number; total: number; repoName: string } | null = null;
  public showAdvancedSections = false;

  private lastProjectId: string | null = null;
  private autoBootstrapStartedForProjectId: string | null = null;
  private autoImportStartedForProjectId: string | null = null;
  private refreshRequestVersion = 0;
  private reposListRequestVersion = 0;
  private installationReposRequestVersion = 0;
  private loadedProjectReposSignature = '';
  private loadedInstallationReposKey = '';

  private repoStateKey(owner: string, name: string): string {
    return `${String(owner ?? '').trim()}/${String(name ?? '').trim()}`;
  }

  private currentProjectId(): string | null {
    return this.reposStore.activeProjectId();
  }

  private isProjectStillActive(projectIdAtStart: string | null): boolean {
    return !!projectIdAtStart && this.currentProjectId() === projectIdAtStart;
  }

  constructor() {
    // Clear any in-memory UI state when switching projects so nothing "leaks" across.
    effect(() => {
      const pid = this.reposStore.activeProjectId();
      if (!pid) return;
      if (this.lastProjectId === pid) return;
      this.lastProjectId = pid;
      this.autoBootstrapStartedForProjectId = null;
      this.autoImportStartedForProjectId = null;
      this.loadedProjectReposSignature = '';
      this.loadedInstallationReposKey = '';

      this.repos = [];
      this.reposError = null;
      this.selectedFullName = '';
      this.installUrl = null;
      this.setupUrlHint = null;
      this.installationRepos = [];
      this.installationReposError = null;
      this.installationSelectedFullName = '';
    });

    effect(() => {
      const projectId = this.reposStore.activeProjectId();
      const installationId = Number(this.reposStore.settings()?.github?.installationId ?? 0) || 0;
      if (!projectId || !installationId) return;
      const key = `${projectId}:${installationId}`;
      if (this.loadedInstallationReposKey === key || this.installationReposLoading) return;
      void this.loadInstallationRepos(true);
    });

    effect(() => {
      const projectId = this.reposStore.activeProjectId();
      const installationId = Number(this.reposStore.settings()?.github?.installationId ?? 0) || 0;
      const refs = this.reposStore.settings()?.github?.repos ?? [];
      if (!projectId || !installationId || refs.length === 0 || this.reposLoading) return;
      const signature = `${projectId}:${installationId}:${refs
        .map((r) => `${r.owner}/${r.name}`)
        .sort()
        .join('|')}`;
      if (this.loadedProjectReposSignature === signature) return;
      void this.loadReposList(true);
    });

    effect(() => {
      const projectId = this.reposStore.activeProjectId();
      const refs = this.reposStore.settings()?.github?.repos ?? [];
      if (!projectId || refs.length === 0 || this.actionBusy) return;
      const parsed = this.parseFullName(this.selectedFullName);
      if (parsed && refs.some((ref) => ref.owner === parsed.owner && ref.name === parsed.name)) return;
      const first = refs[0];
      if (!first) return;
      this.selectedFullName = `${first.owner}/${first.name}`;
      void this.onRepoSelectChange(true);
    });

    // Precompute standard repo names (stable array) to avoid template churn.
    effect(() => {
      const def = this.reposStore.projectDefinition();
      const base = this.slugifyRepoBase(String(def?.name ?? '').trim());
      if (!base) {
        this.standardReposPreview = [];
        return;
      }
      const next = [`${base}-frontend`, `${base}-api`, `${base}-infra`];
      // Only replace the array if it actually changed.
      try {
        if (JSON.stringify(this.standardReposPreview) !== JSON.stringify(next)) {
          this.standardReposPreview = next;
        }
      } catch {
        this.standardReposPreview = next;
      }
    });

    // Best-effort auto-refresh when returning to Repos (keeps PR list fresh).
    effect(() => {
      const installationId = this.reposStore.settings()?.github?.installationId ?? null;
      const last = this.reposStore.state()?.lastRefreshedAt ?? null;
      const focused = this.currentFocusedRepo();
      if (!installationId || !focused) return;
      const lastMs = typeof last === 'number' ? last : null;
      // Refresh if never refreshed, or if stale (>30s).
      const stale = lastMs == null || Date.now() - lastMs > 30_000;
      if (!stale) return;
      void this.refreshAll();
    });

  }

  private parseFullName(v: string): { owner: string; name: string } | null {
    const s = String(v ?? '').trim();
    if (!s) return null;
    const idx = s.indexOf('/');
    if (idx <= 0) return null;
    const owner = s.slice(0, idx).trim();
    const name = s.slice(idx + 1).trim();
    if (!owner || !name) return null;
    return { owner, name };
  }

  private currentFocusedRepo(): { owner: string; name: string } | null {
    const explicit = this.parseFullName(this.selectedFullName);
    if (explicit) return explicit;
    const first = this.reposStore.settings().github.repos?.[0] ?? null;
    return first ? { owner: first.owner, name: first.name } : null;
  }

  private inferRepoRole(owner: string, name: string, rawRole?: RepoRole | string | null): RepoRole {
    const explicit = String(rawRole ?? '').trim().toLowerCase();
    if (explicit === 'site' || explicit === 'api' || explicit === 'agent-api' || explicit === 'infra' || explicit === 'unknown') {
      return explicit as RepoRole;
    }
    const text = `${String(owner ?? '')} ${String(name ?? '')}`.toLowerCase();
    if (/(frontend|site|web|ui|client)/.test(text)) return 'site';
    if (/(agent-api|machine-agent-api|orchestrator|agent)/.test(text)) return 'agent-api';
    if (/(infra|ops|terraform|k8s|deploy)/.test(text)) return 'infra';
    if (/(api|backend|server|service)/.test(text)) return 'api';
    return 'unknown';
  }

  private isMissingPersonalInstallationError(error: any): boolean {
    const message = String(error?.message ?? '').toLowerCase();
    return message.includes('no personal github installation is saved') || (message.includes('"success":false') && message.includes('installationmode=personal'));
  }

  private isInvalidInstallationError(error: any): boolean {
    const message = String(error?.message ?? '').toLowerCase();
    return (
      message.includes('create-an-installation-access-token-for-an-app') ||
      message.includes('/access_tokens') ||
      message.includes('installation access token') ||
      message.includes('not found - https://docs.github.com/rest/reference/apps#create-an-installation-access-token-for-an-app')
    );
  }

  private async recoverToBusinessInstallation(projectIdAtStart: string | null): Promise<boolean> {
    if (!projectIdAtStart || !this.isProjectStillActive(projectIdAtStart)) return false;
    try {
      await this.ensureBusinessInstallation(projectIdAtStart);
      this.loadedProjectReposSignature = '';
      this.loadedInstallationReposKey = '';
      this.installationReposError = null;
      this.reposError = null;
      return true;
    } catch {
      return false;
    }
  }

  private async resolvePersonalInstallationFromIntegrations(): Promise<{ installationId: number; accountLogin?: string } | null> {
    const snapshot: any = await this.integrations.getSnapshot();
    const installationIdCandidates = [
      Number(snapshot?.github?.personal?.installationId ?? 0) || 0,
      Number(snapshot?.github?.resolvedInstallationId ?? 0) || 0,
      Number(snapshot?.github?.business?.installationId ?? 0) || 0,
    ];
    const installationId = installationIdCandidates.find((n) => Number.isFinite(n) && n > 0) ?? 0;
    if (!installationId) return null;
    const accountLogin =
      String(snapshot?.github?.personal?.accountLogin ?? snapshot?.github?.business?.accountLogin ?? '').trim() || undefined;
    return { installationId, accountLogin };
  }

  private resolvePersonalInstallationFromLocal(): { installationId: number; accountLogin?: string } | null {
    try {
      const installationId = Number(localStorage.getItem(ReposWorkspaceComponent.PERSONAL_GITHUB_INSTALLATION_ID_KEY) ?? 0) || 0;
      if (!Number.isFinite(installationId) || installationId <= 0) return null;
      const accountLogin = String(localStorage.getItem(ReposWorkspaceComponent.PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY) ?? '').trim() || undefined;
      return { installationId, accountLogin };
    } catch {
      return null;
    }
  }

  private persistPersonalInstallationToLocal(installationId: number, accountLogin?: string): void {
    if (!Number.isFinite(installationId) || installationId <= 0) return;
    try {
      localStorage.setItem(ReposWorkspaceComponent.PERSONAL_GITHUB_INSTALLATION_ID_KEY, String(installationId));
      if (accountLogin) {
        localStorage.setItem(ReposWorkspaceComponent.PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY, String(accountLogin).trim());
      }
    } catch {
      // Ignore local storage failures.
    }
  }

  public currentInstallationMode(): 'personal' | 'business' {
    const currentInstallationId = Number(this.reposStore.settings()?.github?.installationId ?? 0) || 0;
    const personalInstallationId = this.resolvePersonalInstallationFromLocal()?.installationId ?? 0;
    return currentInstallationId > 0 && personalInstallationId > 0 && currentInstallationId === personalInstallationId ? 'personal' : 'business';
  }

  public currentConnectionLabel(): string {
    if (this.currentInstallationMode() === 'personal') {
      return this.resolvePersonalInstallationFromLocal()?.accountLogin ?? 'your-personal-github';
    }
    return this.businessOwnerLabel;
  }

  public preferredRepoOwnerLabel(): string {
    return this.resolvePersonalInstallationFromLocal()?.accountLogin ?? this.businessOwnerLabel;
  }

  private async syncGitHubTarget(
    installationId: number,
    owner: string,
    installationMode: 'personal' | 'business',
    projectIdAtStart?: string | null
  ): Promise<void> {
    const projectId = projectIdAtStart ?? this.currentProjectId();
    if (projectId && this.isProjectStillActive(projectId)) {
      await this.reposStore.patchSettings(
        {
          github: {
            installationId,
          },
        },
        projectId
      );
    }
    if (installationMode === 'personal') {
      this.persistPersonalInstallationToLocal(installationId, owner);
    } else {
      this.businessOwnerLabel = owner || this.businessOwnerLabel;
    }
    try {
      await this.integrations.updateGithubContext({
        activeInstallationMode: installationMode,
        ...(installationMode === 'personal'
          ? {
              personal: {
                installationId,
                accountLogin: owner || undefined,
              },
            }
          : {
              business: {
                installationId,
                accountLogin: owner || undefined,
              },
            }),
      });
    } catch {
      // Repo operations should still work even if the user-level integration state cannot be updated.
    }
  }

  private async ensureBusinessInstallation(projectIdAtStart?: string | null): Promise<{ installationId: number; owner: string }> {
    const res = await this.github.getDefaultInstallation('business');
    const installationId = Number((res as any)?.installationId ?? 0);
    if (!Number.isFinite(installationId) || installationId <= 0) {
      throw new Error('Invalid business installation id returned');
    }
    const owner = String((res as any)?.accountLogin ?? '').trim() || this.businessOwnerLabel;
    this.businessOwnerLabel = owner;
    await this.syncGitHubTarget(installationId, owner, 'business', projectIdAtStart);
    return { installationId, owner };
  }

  private shouldAutoBootstrapProject(projectId: string | null): boolean {
    if (!projectId) return false;
    try {
      return String(localStorage.getItem(ReposWorkspaceComponent.AUTO_BOOTSTRAP_PROJECT_ID_KEY) ?? '').trim() === projectId;
    } catch {
      return false;
    }
  }

  private clearAutoBootstrapProject(projectId: string | null): void {
    if (!projectId) return;
    try {
      const raw = String(localStorage.getItem(ReposWorkspaceComponent.AUTO_BOOTSTRAP_PROJECT_ID_KEY) ?? '').trim();
      if (raw === projectId) {
        localStorage.removeItem(ReposWorkspaceComponent.AUTO_BOOTSTRAP_PROJECT_ID_KEY);
      }
    } catch {}
  }

  private async connectProjectRepos(
    repoRefs: Array<{ owner: string; name: string }>,
    projectIdAtStart: string,
    successMessage: string,
    quiet = false
  ): Promise<void> {
    if (!this.isProjectStillActive(projectIdAtStart) || repoRefs.length === 0) return;
    const current = this.reposStore.settings().github.repos ?? [];
    const next = [...current, ...repoRefs.map((repo) => ({ ...repo, role: this.inferRepoRole(repo.owner, repo.name) }))]
      .map((repo) => ({
        ...repo,
        role: this.inferRepoRole(repo.owner, repo.name, repo.role),
      }))
      .filter((repo, index, arr) => arr.findIndex((candidate) => candidate.owner === repo.owner && candidate.name === repo.name) === index);
    await this.reposStore.patchSettings(
      {
        github: {
          installationId: this.reposStore.settings().github.installationId,
          repos: next,
        },
      },
      projectIdAtStart
    );
    const focused = this.currentFocusedRepo() ?? repoRefs[0] ?? null;
    if (focused) {
      this.selectedFullName = `${focused.owner}/${focused.name}`;
    }
    this.installationSelectedFullName = this.importableInstallationRepos()[0]?.fullName ?? '';
    await this.loadReposList(true);
    if (focused) {
      await this.refreshAll(true);
    }
    if (!quiet) {
      this.toast.success(successMessage, 'Repos');
    }
  }

  async connectDetectedProjectRepos(quiet = false) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const detected = this.detectedProjectRepos().map((repo) => ({ owner: repo.owner, name: repo.name }));
    if (detected.length === 0) return;
    this.actionBusy = true;
    try {
      await this.connectProjectRepos(detected, projectIdAtStart, 'Connected existing project repos', quiet);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to connect existing project repos', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  private async autoResolveProjectRepos(projectIdAtStart: string): Promise<void> {
    if (!this.isProjectStillActive(projectIdAtStart)) return;
    if ((this.reposStore.settings().github.repos ?? []).length > 0) {
      this.clearAutoBootstrapProject(projectIdAtStart);
      return;
    }
    const detected = this.detectedProjectRepos();
    if (detected.length > 0) {
      if (this.autoImportStartedForProjectId === projectIdAtStart || this.actionBusy) return;
      this.autoImportStartedForProjectId = projectIdAtStart;
      await this.connectDetectedProjectRepos(true);
      this.clearAutoBootstrapProject(projectIdAtStart);
      return;
    }
    if (!this.shouldAutoBootstrapProject(projectIdAtStart)) return;
    if (this.autoBootstrapStartedForProjectId === projectIdAtStart || this.standardBusy || this.actionBusy) return;
    this.autoBootstrapStartedForProjectId = projectIdAtStart;
    this.clearAutoBootstrapProject(projectIdAtStart);
    void this.onCreateStandardRepos();
  }

  private slugifyRepoBase(input: string): string {
    const s = String(input ?? '').trim().toLowerCase();
    if (!s) return '';
    // keep alphanum + dashes; collapse separators
    const cleaned = s
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned.slice(0, 40);
  }

  public standardReposPreview: string[] = [];

  projectReposDisplay(): Array<GitHubRepoSummary | { fullName: string; name: string; owner: string; description?: string }> {
    const refs = this.reposStore.settings().github.repos ?? [];
    return refs.map((ref) => {
      const loaded = this.repos.find((repo) => repo.owner === ref.owner && repo.name === ref.name);
      return (
        loaded ?? {
          fullName: `${ref.owner}/${ref.name}`,
          owner: ref.owner,
          name: ref.name,
          description: undefined,
        }
      );
    });
  }

  get installationReposFiltered(): GitHubRepoSummary[] {
    const q = String(this.installationRepoSearch ?? '').trim().toLowerCase();
    const list = Array.isArray(this.installationRepos) ? this.installationRepos : [];
    if (!q) return list;
    return list.filter((r) => String(r?.fullName ?? '').toLowerCase().includes(q));
  }

  importableInstallationRepos(): GitHubRepoSummary[] {
    const saved = new Set((this.reposStore.settings().github.repos ?? []).map((repo) => `${repo.owner}/${repo.name}`));
    return this.installationReposFiltered.filter((repo) => !saved.has(`${repo.owner}/${repo.name}`));
  }

  detectedProjectRepos(): GitHubRepoSummary[] {
    const expected = new Set((this.standardReposPreview ?? []).map((name) => String(name ?? '').trim()).filter(Boolean));
    if (expected.size === 0) return [];
    return this.importableInstallationRepos().filter((repo) => expected.has(String(repo?.name ?? '').trim()));
  }

  activeOverview(): {
    repo: any | null;
    branches: any[];
    pulls: any[];
    commits: any[];
    status: any | null;
    lastRefreshedAt: number | null;
  } {
    const state = this.reposStore.state();
    const selected = this.parseFullName(this.selectedFullName) ?? this.currentFocusedRepo();
    if (selected) {
      const key = this.repoStateKey(selected.owner, selected.name);
      const entry = (state as any)?.repos?.[key];
      if (entry) {
        return {
          repo: entry.repo ?? null,
          branches: Array.isArray(entry.branches) ? entry.branches : [],
          pulls: Array.isArray(entry.pulls) ? entry.pulls : [],
          commits: Array.isArray(entry.commits) ? entry.commits : [],
          status: entry.status ?? null,
          lastRefreshedAt: typeof entry.lastRefreshedAt === 'number' ? entry.lastRefreshedAt : null,
        };
      }
    }
    return {
      repo: (state as any)?.repo ?? null,
      branches: Array.isArray((state as any)?.branches) ? (state as any).branches : [],
      pulls: Array.isArray((state as any)?.pulls) ? (state as any).pulls : [],
      commits: Array.isArray((state as any)?.commits) ? (state as any).commits : [],
      status: (state as any)?.status ?? null,
      lastRefreshedAt: typeof (state as any)?.lastRefreshedAt === 'number' ? (state as any).lastRefreshedAt : null,
    };
  }

  canCreateStandardRepos(): boolean {
    return (this.standardReposPreview?.length ?? 0) > 0;
  }

  async onCreateStandardRepos() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;

    const names = [...(this.standardReposPreview ?? [])];
    if (names.length === 0) return;

    this.standardBusy = true;
    this.standardProgress = { step: 0, total: names.length, repoName: '' };
    this.actionBusy = true;
    try {
      const def = this.reposStore.projectDefinition();
      const projectName = String(def?.name ?? '').trim() || this.preferredRepoOwnerLabel();
      const projectDesc = String(def?.description ?? '').trim();

      const createdRefs: Array<{ owner: string; name: string }> = [];

      for (let i = 0; i < names.length; i++) {
        const name = names[i]!;
        this.standardProgress = { step: i + 1, total: names.length, repoName: name };

        const repo = await this.github.createRepo({
          installationId: this.reposStore.settings().github.installationId || 0,
          owner: this.preferredRepoOwnerLabel(),
          name,
          private: !!this.createRepo.private,
        });
        const installationId = Number((repo as any)?.installationId ?? 0) || this.reposStore.settings().github.installationId || 0;
        const owner = String((repo as any)?.owner ?? '').trim() || this.preferredRepoOwnerLabel();
        const installationMode = String((repo as any)?.installationMode ?? '').trim() === 'personal' ? 'personal' : 'business';
        await this.syncGitHubTarget(installationId, owner, installationMode, projectIdAtStart);

        // Initialize each repo with a minimal scaffold.
        const kind = name.endsWith('-frontend') ? 'angular' : name.endsWith('-api') ? 'node' : 'infra';
        const readme = [
          '# ' + projectName,
          '',
          projectDesc || 'Project repository managed by Appstrax Machine.',
          '',
          `## Repo: ${name}`,
          '',
        ].join('\n');

        await this.github.upsertFiles({
          installationId,
          owner,
          repo: name,
          message: 'Initialize repository',
          files: [
            { path: 'README.md', contentText: readme },
            { path: '.gitignore', contentText: defaultGitignore(kind) },
            { path: 'LICENSE', contentText: 'UNLICENSED' },
          ],
        });

        const role = this.inferRepoRole(owner, name);
        createdRefs.push({ owner, name });

        // Best-effort: keep UI responsive by updating store as we go.
        const current = this.reposStore.settings().github.repos ?? [];
        const next = [...current, { owner, name, role }].map((repo) => ({
          ...repo,
          role: this.inferRepoRole(repo.owner, repo.name, repo.role),
        })).filter(
          (r, i, arr) => arr.findIndex((x) => x.owner === r.owner && x.name === r.name) === i
        );
        if (!this.isProjectStillActive(projectIdAtStart)) return;
        await this.reposStore.patchSettings({
          github: {
            installationId,
            repos: next,
          },
        }, projectIdAtStart);
      }

      if (!this.isProjectStillActive(projectIdAtStart)) return;
      await this.loadReposList();

      // Focus the first created repo by default if no local focus exists.
      if (!this.currentFocusedRepo() && createdRefs.length > 0) {
        const first = createdRefs[0]!;
        this.selectedFullName = `${first.owner}/${first.name}`;
        await this.onRepoSelectChange();
      }

      this.toast.success('Standard repos created', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to create standard repos', 'Repos');
    } finally {
      this.actionBusy = false;
      this.standardBusy = false;
      this.standardProgress = null;
    }
  }

  canAddSelectedRepoToProject(): boolean {
    const parsed = this.parseFullName(this.selectedFullName);
    if (!parsed) return false;
    const list = this.reposStore.settings().github.repos ?? [];
    return !list.some((r) => r.owner === parsed.owner && r.name === parsed.name);
  }

  canAddInstallationSelectedRepoToProject(): boolean {
    const parsed = this.parseFullName(this.installationSelectedFullName);
    if (!parsed) return false;
    const list = this.reposStore.settings().github.repos ?? [];
    return !list.some((r) => r.owner === parsed.owner && r.name === parsed.name);
  }

  async addInstallationSelectedRepoToProject() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const installationId = this.reposStore.settings().github.installationId;
    if (!installationId) return;
    const parsed = this.parseFullName(this.installationSelectedFullName);
    if (!parsed) return;

    this.actionBusy = true;
    try {
      // Validate access (and surface a clean error) before persisting.
      await this.github.getRepo(installationId, parsed.owner, parsed.name);
      await this.connectProjectRepos([parsed], projectIdAtStart, 'Added repo to project');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to add repo to project', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  public repoRole: Record<string, RepoRole> = {};

  private keyFor(owner: string, name: string): string {
    return `${String(owner ?? '').trim()}/${String(name ?? '').trim()}`;
  }

  public roleFor(owner: string, name: string): RepoRole {
    const k = this.keyFor(owner, name);
    const explicit = this.repoRole[k];
    if (explicit) return explicit;
    const fromSettings = (this.reposStore.settings().github.repos ?? []).find((r: any) => r.owner === owner && r.name === name) as any;
    const role = this.inferRepoRole(owner, name, fromSettings?.role);
    return role || 'unknown';
  }

  public setRoleFor(owner: string, name: string, role: RepoRole) {
    const k = this.keyFor(owner, name);
    this.repoRole[k] = role;
  }

  async saveRepoRoles() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const current = this.reposStore.settings().github.repos ?? [];
    const next = current.map((r: any) => {
      const k = this.keyFor(r.owner, r.name);
      const role = this.repoRole[k] ?? this.inferRepoRole(r.owner, r.name, r.role);
      return role ? { ...r, role } : { ...r };
    });
    await this.reposStore.patchSettings({
      github: {
        installationId: this.reposStore.settings().github.installationId,
        repos: next as any,
      },
    }, projectIdAtStart);
    this.toast.success('Saved repo roles', 'Repos');
  }

  async addSelectedRepoToProject() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const parsed = this.parseFullName(this.selectedFullName);
    if (!parsed) return;
    const current = this.reposStore.settings().github.repos ?? [];
    const next = [...current, { ...parsed, role: this.inferRepoRole(parsed.owner, parsed.name) }]
      .map((repo) => ({ ...repo, role: this.inferRepoRole(repo.owner, repo.name, repo.role) }))
      .filter((r, i, arr) => arr.findIndex((x) => x.owner === r.owner && x.name === r.name) === i);
    await this.reposStore.patchSettings({
      github: {
        installationId: this.reposStore.settings().github.installationId,
        repos: next,
      },
    }, projectIdAtStart);
    this.toast.success('Added repo to project', 'Repos');
  }

  async removeProjectRepo(owner: string, name: string) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const current = this.reposStore.settings().github.repos ?? [];
    const next = current.filter((r) => r.owner !== owner || r.name !== name);
    const selected = this.currentFocusedRepo();
    const isSelected = !!selected && selected.owner === owner && selected.name === name;
    await this.reposStore.patchSettings({
      github: {
        installationId: this.reposStore.settings().github.installationId,
        repos: next,
      },
    }, projectIdAtStart);
    if (isSelected) {
      this.selectedFullName = '';
      await this.reposStore.patchState({ repo: null, pulls: [], commits: [], status: null, lastRefreshedAt: Date.now() }, projectIdAtStart);
    }
    this.toast.success('Removed repo from project', 'Repos');
  }

  async selectProjectRepo(owner: string, name: string) {
    this.selectedFullName = `${owner}/${name}`;
    await this.onRepoSelectChange();
  }

  async onInstallClick(returnState: 'repos' | 'settings' = 'repos') {
    this.installLoading = true;
    try {
      const res = await this.github.getInstallUrl();
      const joiner = res.url.includes('?') ? '&' : '?';
      const installUrl = `${res.url}${joiner}state=${encodeURIComponent(returnState)}`;
      try {
        localStorage.setItem(ReposWorkspaceComponent.PENDING_INSTALL_SOURCE_KEY, returnState);
      } catch {}
      this.installUrl = installUrl;
      this.setupUrlHint = res.setupUrlHint ?? null;
      window.open(installUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to fetch install URL', 'Repos');
    } finally {
      this.installLoading = false;
    }
  }

  async onUseOrganizationClick() {
    this.actionBusy = true;
    try {
      await this.ensureBusinessInstallation(this.currentProjectId());
      await this.loadInstallationRepos(true);
      this.toast.success('Using organization GitHub for this project', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to switch to organization GitHub', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  async onConnectExistingClick() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    this.connectExistingLoading = true;
    try {
      let res: any;
      try {
        res = await this.github.getDefaultInstallation('personal');
      } catch (primaryError: any) {
        if (!this.isMissingPersonalInstallationError(primaryError)) throw primaryError;
        const fallback = await this.resolvePersonalInstallationFromIntegrations();
        if (fallback) {
          res = fallback;
          this.toast.info('Recovered personal installation from your saved integrations', 'Repos');
        } else {
          const localFallback = this.resolvePersonalInstallationFromLocal();
          if (localFallback) {
            res = localFallback;
            this.toast.info('Recovered personal installation from local settings', 'Repos');
          } else {
            await this.onInstallClick('repos');
            this.toast.info('Finish connecting your GitHub App installation, then return here.', 'Repos');
            return;
          }
        }
      }
      const installationId = Number((res as any)?.installationId);
      if (!Number.isFinite(installationId) || installationId <= 0) {
        throw new Error('Invalid installation id returned');
      }
      const accountLogin = String((res as any)?.accountLogin ?? '').trim();
      await this.syncGitHubTarget(installationId, accountLogin, 'personal', projectIdAtStart);
      await this.loadInstallationRepos(true);
      this.toast.success('Personal GitHub connected for this project', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to connect existing installation', 'Repos');
    } finally {
      this.connectExistingLoading = false;
    }
  }

  async loadReposList(quiet = false, allowRecovery = true) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const requestVersion = ++this.reposListRequestVersion;
    const installationId = this.reposStore.settings().github.installationId;
    if (!installationId) return;
    this.reposLoading = true;
    this.reposError = null;
    try {
      const refs = this.reposStore.settings().github.repos ?? [];
      if (refs.length === 0) {
        if (!this.isProjectStillActive(projectIdAtStart) || requestVersion !== this.reposListRequestVersion) return;
        this.repos = [];
        this.loadedProjectReposSignature = '';
        return;
      }

      const settled = await Promise.allSettled(
        refs.map((r) => this.github.getRepo(installationId, r.owner, r.name))
      );

      const ok = settled
        .filter((x): x is PromiseFulfilledResult<any> => x.status === 'fulfilled')
        .map((x) => x.value)
        .filter(Boolean) as GitHubRepoSummary[];

      const failed = settled.filter((x) => x.status === 'rejected');
      if (
        allowRecovery &&
        failed.length > 0 &&
        failed.length === settled.length &&
        failed.every((x: any) => this.isInvalidInstallationError(x?.reason))
      ) {
        const recovered = await this.recoverToBusinessInstallation(projectIdAtStart);
        if (recovered) {
          await this.loadInstallationRepos(true, false);
          await this.loadReposList(quiet, false);
          return;
        }
      }
      if (failed.length > 0) {
        this.reposError = `Failed to load ${failed.length} repo${failed.length === 1 ? '' : 's'}`;
      }

      if (!this.isProjectStillActive(projectIdAtStart) || requestVersion !== this.reposListRequestVersion) return;
      // Sort for stable UX.
      this.repos = ok.sort((a, b) => String(a.fullName ?? '').localeCompare(String(b.fullName ?? '')));
      this.loadedProjectReposSignature = `${projectIdAtStart}:${installationId}:${refs
        .map((r) => `${r.owner}/${r.name}`)
        .sort()
        .join('|')}`;

      // Keep per-repo overview cache in sync with known metadata.
      const currentState: any = this.reposStore.state();
      const currentMap = currentState?.repos && typeof currentState.repos === 'object' ? currentState.repos : {};
      const nextMap: Record<string, any> = { ...currentMap };
      for (const r of this.repos) {
        const key = this.repoStateKey(String((r as any)?.owner ?? ''), String((r as any)?.name ?? ''));
        if (!key || key === '/') continue;
        const prev = nextMap[key] ?? {};
        nextMap[key] = {
          repo: r,
          branches: Array.isArray(prev?.branches) ? prev.branches : [],
          pulls: Array.isArray(prev?.pulls) ? prev.pulls : [],
          commits: Array.isArray(prev?.commits) ? prev.commits : [],
          status: prev?.status ?? null,
          lastRefreshedAt: typeof prev?.lastRefreshedAt === 'number' ? prev.lastRefreshedAt : null,
        };
      }
      await this.reposStore.patchState({ repos: nextMap } as any, projectIdAtStart);
    } catch (e: any) {
      if (allowRecovery && this.isInvalidInstallationError(e)) {
        const recovered = await this.recoverToBusinessInstallation(projectIdAtStart);
        if (recovered) {
          await this.loadInstallationRepos(true, false);
          await this.loadReposList(quiet, false);
          return;
        }
      }
      this.reposError = e?.message ?? 'Failed to load project repositories';
      this.loadedProjectReposSignature = '';
      if (!quiet) {
        this.toast.error(this.reposError || 'Failed to load project repositories', 'Repos');
      }
    } finally {
      this.reposLoading = false;
    }
  }

  async loadInstallationRepos(quiet = false, allowRecovery = true) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const requestVersion = ++this.installationReposRequestVersion;
    const installationId = this.reposStore.settings().github.installationId;
    if (!installationId) return;
    this.installationReposLoading = true;
    this.installationReposError = null;
    try {
      const res = await this.github.listRepos(installationId);
      const repos = Array.isArray((res as any)?.repositories) ? ((res as any).repositories as GitHubRepoSummary[]) : [];
      if (!this.isProjectStillActive(projectIdAtStart) || requestVersion !== this.installationReposRequestVersion) return;
      this.installationRepos = repos.sort((a, b) => String(a.fullName ?? '').localeCompare(String(b.fullName ?? '')));
      this.installationSelectedFullName = this.importableInstallationRepos()[0]?.fullName ?? '';
      this.loadedInstallationReposKey = `${projectIdAtStart}:${installationId}`;
      await this.autoResolveProjectRepos(projectIdAtStart);
      if (!quiet) {
        this.toast.success('Loaded available repositories', 'Repos');
      }
    } catch (e: any) {
      if (allowRecovery && this.isInvalidInstallationError(e)) {
        const recovered = await this.recoverToBusinessInstallation(projectIdAtStart);
        if (recovered) {
          await this.loadInstallationRepos(quiet, false);
          return;
        }
      }
      this.installationReposError = e?.message ?? 'Failed to load installation repositories';
      this.loadedInstallationReposKey = '';
      if (!quiet) {
        this.toast.error(this.installationReposError || 'Failed to load installation repositories', 'Repos');
      }
    } finally {
      this.installationReposLoading = false;
    }
  }

  async onRepoSelectChange(quiet = false) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const v = String(this.selectedFullName ?? '').trim();
    if (!v) {
      return;
    }
    const idx = v.indexOf('/');
    if (idx <= 0) return;
    await this.refreshAll(quiet);
  }

  async refreshAll(quiet = false, allowRecovery = true) {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const requestVersion = ++this.refreshRequestVersion;
    const installationId = this.reposStore.settings().github.installationId;
    const repoRef = this.currentFocusedRepo();
    if (!installationId || !repoRef) return;

    this.refreshing = true;
    try {
      const [repo, branches, pulls, commits, status] = await Promise.all([
        this.github.getRepo(installationId, repoRef.owner, repoRef.name),
        this.github.listBranches(installationId, repoRef.owner, repoRef.name),
        this.github.listPulls(installationId, repoRef.owner, repoRef.name, 'open'),
        this.github.listCommits(installationId, repoRef.owner, repoRef.name),
        this.github.getStatus(installationId, repoRef.owner, repoRef.name, undefined),
      ]);
      if (!this.isProjectStillActive(projectIdAtStart) || requestVersion !== this.refreshRequestVersion) return;

      const stateNow: any = this.reposStore.state();
      const mapNow = stateNow?.repos && typeof stateNow.repos === 'object' ? stateNow.repos : {};
      const repoKey = this.repoStateKey(repoRef.owner, repoRef.name);
      const nextMap = {
        ...mapNow,
        [repoKey]: {
          repo,
          branches,
          pulls,
          commits,
          status,
          lastRefreshedAt: Date.now(),
        },
      };

      await this.reposStore.patchState({
        repo,
        branches,
        pulls,
        commits,
        status,
        repos: nextMap as any,
        lastRefreshedAt: Date.now(),
      }, projectIdAtStart);

      // Seed defaults for branch/PR actions.
      const defaultBranch = String((repo as any)?.defaultBranch ?? 'main').trim() || 'main';
      this.branch.baseBranch = defaultBranch;
      this.pr.base = defaultBranch;
      if (!quiet) {
        this.toast.success('Repo refreshed', 'Repos');
      }
    } catch (e: any) {
      if (allowRecovery && this.isInvalidInstallationError(e)) {
        const recovered = await this.recoverToBusinessInstallation(projectIdAtStart);
        if (recovered) {
          await this.loadInstallationRepos(true, false);
          await this.loadReposList(true, false);
          await this.refreshAll(quiet, false);
          return;
        }
      }
      if (!quiet) {
        this.toast.error(e?.message ?? 'Failed to refresh repo', 'Repos');
      }
    } finally {
      this.refreshing = false;
    }
  }

  async onCreateRepo() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const name = String(this.createRepo.name ?? '').trim();
    if (!name) {
      this.toast.warning('Repo name is required', 'Repos');
      return;
    }

    this.actionBusy = true;
    try {
      const repo = await this.github.createRepo({
        installationId: this.reposStore.settings().github.installationId || 0,
        owner: this.preferredRepoOwnerLabel(),
        name,
        description: String(this.createRepo.description ?? '').trim() || undefined,
        private: !!this.createRepo.private,
      });
      const installationId = Number((repo as any)?.installationId ?? 0) || this.reposStore.settings().github.installationId || 0;
      const owner = String((repo as any)?.owner ?? '').trim() || this.preferredRepoOwnerLabel();
      const installationMode = String((repo as any)?.installationMode ?? '').trim() === 'personal' ? 'personal' : 'business';
      await this.syncGitHubTarget(installationId, owner, installationMode, projectIdAtStart);

      const full = `${owner}/${name}`;
      this.selectedFullName = full;

      // Ensure the new repo is saved to THIS project's repo list.
      const current = this.reposStore.settings().github.repos ?? [];
      const next = [...current, { owner, name, role: this.inferRepoRole(owner, name) }]
        .map((repo) => ({ ...repo, role: this.inferRepoRole(repo.owner, repo.name, repo.role) }))
        .filter(
        (r, i, arr) => arr.findIndex((x) => x.owner === r.owner && x.name === r.name) === i
      );
      if (!this.isProjectStillActive(projectIdAtStart)) return;
      await this.reposStore.patchSettings({
        github: {
          installationId,
          repos: next,
        },
      }, projectIdAtStart);

      await this.loadReposList(true);
      await this.reposStore.patchState({ repo, lastRefreshedAt: Date.now() }, projectIdAtStart);
      this.toast.success('Repository created', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to create repo', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  async onInitRepo() {
    const installationId = this.reposStore.settings().github.installationId;
    const repoRef = this.currentFocusedRepo();
    if (!installationId || !repoRef) return;
    this.actionBusy = true;
    try {
      const def = this.reposStore.projectDefinition();
      const projectName = String(def?.name ?? '').trim() || `${repoRef.owner}/${repoRef.name}`;
      const projectDesc = String(def?.description ?? '').trim();

      const readme = ['# ' + projectName, '', projectDesc || 'Project repository managed by Appstrax Machine.', '', '## Quickstart', '- TODO', ''].join('\n');

      await this.github.upsertFiles({
        installationId,
        owner: repoRef.owner,
        repo: repoRef.name,
        message: 'Initialize repository',
        files: [
          { path: 'README.md', contentText: readme },
          { path: '.gitignore', contentText: defaultGitignore('angular') },
          { path: 'LICENSE', contentText: 'UNLICENSED' },
        ],
      });

      await this.refreshAll();
      this.toast.success('Repo initialized', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to initialize repo', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  async onCreateBranch() {
    const installationId = this.reposStore.settings().github.installationId;
    const repoRef = this.currentFocusedRepo();
    if (!installationId || !repoRef) return;
    const baseBranch = String(this.branch.baseBranch ?? '').trim() || 'main';
    const name = String(this.branch.name ?? '').trim();
    if (!name) {
      this.toast.warning('Branch name is required', 'Repos');
      return;
    }
    this.actionBusy = true;
    try {
      await this.github.createBranch({ installationId, owner: repoRef.owner, repo: repoRef.name, baseBranch, name });
      // Convenience: seed follow-up actions.
      this.upsert.branch = name;
      this.pr.head = name;
      this.toast.success('Branch created', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to create branch', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  async onUpsertFile() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const installationId = this.reposStore.settings().github.installationId;
    const repoRef = this.currentFocusedRepo();
    if (!installationId || !repoRef) return;

    const branch = String(this.upsert.branch ?? '').trim();
    const path = String(this.upsert.path ?? '').trim();
    const message = String(this.upsert.message ?? '').trim();
    const contentText = String(this.upsert.contentText ?? '');

    if (!branch) {
      this.toast.warning('Branch is required', 'Repos');
      return;
    }
    if (!path) {
      this.toast.warning('File path is required', 'Repos');
      return;
    }
    if (!message) {
      this.toast.warning('Commit message is required', 'Repos');
      return;
    }
    if (!contentText.trim()) {
      this.toast.warning('File contents are required', 'Repos');
      return;
    }

    this.actionBusy = true;
    try {
      await this.github.upsertFiles({
        installationId,
        owner: repoRef.owner,
        repo: repoRef.name,
        branch,
        message,
        files: [{ path, contentText }],
      });
      if (!this.isProjectStillActive(projectIdAtStart)) return;
      this.toast.success('Committed file', 'Repos');
      await this.refreshAll();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to commit file', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }

  async onCreatePr() {
    const projectIdAtStart = this.currentProjectId();
    if (!projectIdAtStart) return;
    const installationId = this.reposStore.settings().github.installationId;
    const repoRef = this.currentFocusedRepo();
    if (!installationId || !repoRef) return;
    const title = String(this.pr.title ?? '').trim();
    const head = String(this.pr.head ?? '').trim();
    const base = String(this.pr.base ?? '').trim();
    if (!title || !head || !base) {
      this.toast.warning('Title, head, and base are required', 'Repos');
      return;
    }
    this.actionBusy = true;
    try {
      const created = await this.github.createPull({
        installationId,
        owner: repoRef.owner,
        repo: repoRef.name,
        title,
        head,
        base,
        body: String(this.pr.body ?? '').trim() || undefined,
      });
      if (!this.isProjectStillActive(projectIdAtStart)) return;
      this.toast.success(`PR created (#${created?.number ?? ''})`, 'Repos');
      await this.refreshAll();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to create PR', 'Repos');
    } finally {
      this.actionBusy = false;
    }
  }
}

